import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import eventsData from '../data/events.json';
import vacanciesData from '../data/vacancies.json';
import articlesData from '../data/articles.json';
import { formatDate } from '../utils/dateFormat';

type Event = {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  image: string;
  ticketLink: string;
  showOnHomePage?: boolean;
};

type Vacancy = {
  id: number;
  title: string;
  description: string;
  requirements: string[];
  postedDate: string;
  closingDate: string;
};

type Article = {
  id: number;
  title: string;
  date: string;
  content: string;
  image: string;
  author: string;
  showOnHomePage?: boolean;
  summary: string;
};

const getInstrumentIcon = (title: string): string => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('cornet')) return 'trumpet';
  if (titleLower.includes('trombone')) return 'trombone';
  if (titleLower.includes('euphonium')) return 'trumpet';
  if (titleLower.includes('tuba')) return 'trumpet';
  if (titleLower.includes('horn')) return 'trumpet';
  if (titleLower.includes('percussion')) return 'drum';
  if (titleLower.includes('drum')) return 'drum';
  return 'music';
};

const Home = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    // Filter events that should be shown on home page
    const homeEvents = (eventsData.upcomingEvents as unknown as Event[]).filter(event => event.showOnHomePage === true);
    setEvents(homeEvents);

    // Show all vacancies without filtering
    setVacancies(vacanciesData.vacancies as unknown as Vacancy[]);

    // Filter articles that should be shown on home page
    const homeArticles = (articlesData.articles as unknown as Article[]).filter(article => article.showOnHomePage === true);
    setArticles(homeArticles);
  }, []);

  return (
    <Container className="home">
      {/* Hero Section */}
      <div className="hero-section text-center py-5 mb-5">
        <h1 className="display-4 mb-4" style={{ fontFamily: 'Source Sans Pro, sans-serif' }}>
          Welcome to East London Brass
        </h1>
        <p className="lead mb-4">London and Southern Counties Championship Section Winners 2025</p>
        <Link to="about" className="btn btn-primary btn-lg">
          Learn More
        </Link>
      </div>

      {/* Featured Content */}
      <Row className="g-4">
        {events.length > 0 && (
          <Col md={4}>
            <Card className="h-100">
              {events[0].image && (
                <Card.Img 
                  variant="top" 
                  src={events[0].image} 
                  alt={events[0].title}
                  style={{ height: '200px', objectFit: 'cover' }}
                />
              )}
              <Card.Body className="d-flex flex-column" style={{ minHeight: '200px' }}>
                <Card.Title>Upcoming Events</Card.Title>
                {events.map((event, index) => (
                  <div key={event.id} className={`d-flex flex-column ${index > 0 ? 'mt-3' : ''}`} style={{ flex: 1 }}>
                    <Card.Text>
                      <strong>{event.title}</strong>
                      <br />
                      <span className="text-accent">When:</span> {formatDate(event.date)} at {event.time}
                      <br />
                      <span className="text-accent">Where:</span> {event.location}
                      <br />
                      {event.description}
                    </Card.Text>
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <Link to={`/events`} className="btn btn-primary w-auto" style={{ zIndex: 1001 }}>
                        View Events
                      </Link>
                      {event.ticketLink && (
                        <a 
                          href={event.ticketLink} 
                          className="btn btn-success w-auto"
                          style={{ zIndex: 1001 }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Buy Tickets
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        )}
        
        {vacancies.length > 0 && (
          <Col md={4}>
            <Card className="h-100">
              <div className="text-center p-4" style={{ backgroundColor: '#000000', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-user-plus fa-5x" style={{ color: '#C0C0C0' }}></i>
              </div>
              <Card.Body className="d-flex flex-column" style={{ minHeight: '270px' }}>
                <Card.Title>Join Us!</Card.Title>
                <Card.Subtitle className="mb-3 text-muted">We are looking for the following players:</Card.Subtitle>
                <Card.Text>
                  <ul className="list-unstyled">
                    {vacancies.map((vacancy) => (
                      <li key={vacancy.id} className="mb-3">
                        <strong>{vacancy.title}</strong>
                      </li>
                    ))}
                  </ul>
                </Card.Text>
                <div className="d-flex justify-content-start mt-auto">
                  <Link to="/vacancies" className="btn btn-primary w-auto">
                    View Current Vacancies
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
        
        {articles.map((article) => (
          <Col md={4} key={article.id}>
            <Card className="h-100">
              {article.image && (
                <Card.Img 
                  variant="top" 
                  src={article.image} 
                  alt={article.title}
                  style={{ height: '200px', objectFit: 'cover' }}
                />
              )}
              <Card.Body className="d-flex flex-column" style={{ minHeight: '270px' }}>
                <Card.Title>Latest News</Card.Title>
                <Card.Text>
                  <strong>{article.title}</strong>
                  <br />
                  {formatDate(article.date)}
                  <br />
                  <div dangerouslySetInnerHTML={{ __html: article.summary }} />
                </Card.Text>
                <div className="d-flex justify-content-between align-items-center mt-auto">
                  <Link to={`/articles/${article.id}`} className="btn btn-primary w-auto">
                    Read More
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default Home; 